/**
 * useAdhocProposalFlow — chat "draft content" intent, decision half.
 *
 * State machine (mirrors useHighlightEditFlow, minus "requesting" —
 * the drafting itself runs server-side and lands via SSE):
 *   idle → pending_decision → applying → idle
 *
 * A proposal arrives from either producer:
 *   - live: the ``adhoc_draft_ready`` SSE event (shell calls ``offer``)
 *   - rehydration: an undecided ``pending_adhoc_proposal`` in the
 *     sections/state snapshot after a pane reload
 * and renders as an Approve/Reject ProposalCard in the thread.
 *
 * A ``draft`` proposal is a FRAGMENT and is ADDITIVE: it is spliced into
 * the section after ``anchorText`` (the paragraph the writer's cursor was
 * in at send time) and nothing already written is touched. Only a
 * ``redraft`` — reachable solely from the explicit /regen command —
 * replaces a whole body. Chat cannot rewrite a section.
 *
 * Approve is SSOT-FIRST (the mirror image of the legacy Insert path):
 *   1. Mongo — the decision endpoint walks the lifecycle, splices (draft)
 *      or replaces (redraft) via the version-checked ``edited``
 *      transition, and stamps the proposal decided.
 *   2. Word — only after the SSOT commit,
 *      ``insertFragmentInSection`` / ``insertSectionContent`` writes the
 *      same change into the document. Both sides match the anchor the
 *      same way, so they land at the same paragraph. A Word failure here
 *      leaves the SSOT correct and the doc stale — surfaced honestly
 *      ("use Load to refresh"), never rolled back.
 *
 * Version drift (writer edited the section after the draft was made)
 * is surfaced as a warning and requires a second, deliberate Approve
 * click armed with the fresh version — never a silent retry. It matters
 * less for a fragment than it did for a replace, but an approve is still
 * the writer's decision to make against what is actually there now.
 */

import { useCallback, useRef, useState } from "react";
import type { MentionAttachment } from "../../types";
import {
  type FragmentDraftResult,
  decideAdhocProposal,
  getSectionContent,
  getSectionState,
  requestFragmentDraftStream,
} from "../../api/client";
import { endNoteOverwriteMessage } from "../../services/endnoteGuard";
import {
  countEndNoteFieldsIn,
  insertFragmentInSection,
  insertSectionContent,
  refreshLiveDocFields,
} from "../../services/wordWriter";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import type { ApplyStep } from "../components/ApplyProgress";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import { labelForSelectionEditEvent } from "./selectionEditLabels";
import { walkToInserted } from "../services/proposalInsert";
import { submitAgentRedraft } from "../services/writeBackBridge";

// Post-approve apply stages, in execution order for the ad-hoc flow:
// SSOT commit first, then the Word render (the slow part). Reject also
// enters "applying" but does no Word write, so it shows no checklist.
const APPLY_STEP_SAVE = "Saving to the record";
const APPLY_STEP_WORD = "Applying in the Word document";

/**
 * Insert ``fragment`` into ``draft`` as its own block, right after the first
 * whitespace-tolerant match of ``anchor``. Client mirror of the server's
 * ``insert_after_span_in_draft`` (server/resolve_span.py) — kept in step with
 * it the same way ``replaceSpanInDraft`` mirrors ``replace_span_in_draft``.
 *
 * Only the ``no_pending_proposal`` recovery path uses this; the normal approve
 * lets the server splice, so there is one writer of the stored body. An empty
 * or unfound anchor appends, so the writer's content is never dropped.
 *
 * Exported for tests.
 */
export function insertAfterSpanInDraft(draft: string, anchor: string, fragment: string): string {
  const body = fragment.trim();
  if (!body) return draft;
  const needle = anchor.replace(/\s+/g, " ").trim();
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/ /g, "\\s+");
  const match = needle ? new RegExp(escaped).exec(draft) : null;
  if (match === null) {
    return draft.trim() ? `${draft.replace(/\s+$/, "")}\n\n${body}\n` : `${body}\n`;
  }
  // Extend past the rest of the anchor's block, so a partially-matched or
  // wrapped anchor paragraph doesn't get split mid-sentence.
  const blank = /\n[ \t]*\n/g;
  blank.lastIndex = match.index + match[0].length;
  const after = blank.exec(draft);
  const cut = after ? after.index : draft.length;
  const head = draft.slice(0, cut).replace(/\s+$/, "");
  const tail = draft.slice(cut).replace(/^\n+/, "");
  // No tail means the anchor was the last block — a single trailing newline,
  // so the body doesn't grow a gap each time something is appended.
  return tail ? `${head}\n\n${body}\n\n${tail}` : `${head}\n\n${body}\n`;
}

export interface AdhocProposal {
  runId: string;
  moduleId: string;
  /** Numbered section (e.g. "9.2") the Word insert targets; null when
   *  the module couldn't be resolved to a skeleton section. */
  sectionNumber: string | null;
  /** Writer-facing label, e.g. "§9.2" or the module title. */
  sectionLabel: string;
  instruction: string;
  content: string;
  wordCount: number;
  baseSectionVersion: number | null;
  /** "draft" = ad-hoc content (instruction overrode the module
   *  instructions) — a FRAGMENT inserted at ``anchorText``, never a
   *  body replace; "redraft" = full section redraft via /regen (writer
   *  changes additive to the configured instructions, regen bookkeeping
   *  on approve, and the only mode that replaces the whole body).
   *  Drives card labels and server-side bookkeeping on approve. */
  mode: "draft" | "redraft";
  /** ``draft`` mode: the paragraph the fragment is inserted after. Null =
   *  no cursor paragraph resolved, so it lands at the end of the section. */
  anchorText: string | null;
  /** The section's current body, when the producer knows it (Insert from
   *  source) — the review shows the change against it. */
  originalText?: string | null;
  /** ``redraft`` copied from a Word source (Insert from source): the section
   *  as a Word file (base64 .docx). Word inserts it as it is, so tables keep
   *  their style, lists their bullets and figures their place; ``content``
   *  (the record's Markdown) is the fallback when the insert fails. */
  sourceDocx?: string | null;
}

export type AdhocProposalPhase = "idle" | "requesting" | "pending_decision" | "applying";

/** Result of the shared commit-then-write core. ``null`` (returned directly by
 *  ``commitAndWrite``) means a newer flow superseded this one mid-await. */
type CommitOutcome =
  | { kind: "applied"; note: string }
  | { kind: "conflict"; currentVersion: number }
  | { kind: "error"; message: string };

/** Snapshot handed to ``onDecisionRecorded`` so the shell can append
 *  the durable ``adhoc_draft`` EventLog entry with the outcome baked in. */
export interface AdhocProposalDecisionRecord {
  moduleId: string;
  sectionNumber: string | null;
  instruction: string;
  content: string;
  wordCount: number;
  mode: "draft" | "redraft";
}

export interface UseAdhocProposalFlowParams {
  /** Record the decision in the EventLog (shell owns dispatch). */
  onDecisionRecorded: (decision: "accepted" | "rejected", note: string, record: AdhocProposalDecisionRecord) => void;
  /** Surface a writer-readable error (rendered as an error note). */
  onError: (message: string) => void;
}

export interface UseAdhocProposalFlowResult {
  phase: AdhocProposalPhase;
  /** The active proposal while phase === "pending_decision" | "applying". */
  proposal: AdhocProposal | null;
  /** Single status line while phase === "requesting" — what the drafting
   *  agent is doing right now (retrieving X, drafting…). Mapped from the
   *  stream by the shared ``labelForSelectionEditEvent``. */
  progressLabel: string | null;
  /** Set when the last Approve hit version drift — rendered as the
   *  card's warning; the next Approve overwrites deliberately. */
  driftWarning: string | null;
  /** Stepped checklist for the post-approve apply pipeline (save →
   *  Word). Empty unless an APPROVE is in flight — a reject enters
   *  "applying" too but does no Word write, so it shows no checklist. */
  applySteps: ApplyStep[];
  /** Offer a proposal (from SSE or rehydration). Replaces any current
   *  card — latest wins. */
  offer: (proposal: AdhocProposal) => void;
  /** Auto-apply a proposal with NO Approve/Reject card — commit SSOT + write
   *  the section into the live doc immediately (the same pipeline Approve runs).
   *  Used for the chat ``/redraft`` auto-load path. On version drift it falls
   *  back to ``offer`` (the manual card) so a later edit is never silently
   *  overwritten; it also falls back to the card when a decision is already in
   *  flight. */
  autoApply: (proposal: AdhocProposal) => Promise<void>;
  /** Chat "draft content": run the in-request drafting stream and, on
   *  completion, offer the fragment as a card. Owns the ``requesting`` phase
   *  and the abort controller behind ``cancel``. */
  requestFragment: (req: FragmentDraftRequest) => Promise<void>;
  /** Stop an in-flight draft. Aborts the stream, which the route sees as a
   *  disconnect and turns into a cooperative agent cancel. */
  cancel: () => void;
  approve: () => Promise<void>;
  reject: () => Promise<void>;
}

/** What the shell hands ``requestFragment`` to start a draft. */
export interface FragmentDraftRequest {
  runId: string;
  moduleId: string;
  sectionNumber: string | null;
  sectionLabel: string;
  instruction: string;
  anchorText: string | null;
  libraryId?: string | null;
  docType?: string | null;
  attachments?: MentionAttachment[];
}

export function useAdhocProposalFlow(params: UseAdhocProposalFlowParams): UseAdhocProposalFlowResult {
  const { onDecisionRecorded, onError } = params;
  const [phase, setPhase] = useState<AdhocProposalPhase>("idle");
  const [proposal, setProposal] = useState<AdhocProposal | null>(null);
  const [driftWarning, setDriftWarning] = useState<string | null>(null);
  // Post-approve apply progress. ``applyLabels`` is populated only by the
  // approve path (empty for reject), so the checklist shows only when a
  // Word write is actually happening.
  const [applyLabels, setApplyLabels] = useState<string[]>([]);
  const [applyStepIndex, setApplyStepIndex] = useState(-1);
  // Monotonic token so a stale async result can't resurrect a flow the
  // writer already decided / a newer proposal already replaced.
  const flowTokenRef = useRef(0);
  // Single status line while a draft is streaming (null otherwise).
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  // Abort handle for the in-flight fragment-draft stream. The flow token above
  // only makes a late response IGNORED; this stops it being produced at all —
  // the route polls request.is_disconnected and cancels the agent.
  const abortRef = useRef<AbortController | null>(null);
  // After a version_conflict 409 this holds the server's current
  // version — the next Approve submits it, making the overwrite an
  // explicit two-click decision.
  const overrideVersionRef = useRef<number | null>(null);

  const offer = useCallback((next: AdhocProposal) => {
    flowTokenRef.current++;
    overrideVersionRef.current = null;
    setApplyLabels([]);
    setApplyStepIndex(-1);
    setProposal(next);
    setDriftWarning(null);
    setPhase("pending_decision");
  }, []);

  const record = useCallback(
    (p: AdhocProposal): AdhocProposalDecisionRecord => ({
      moduleId: p.moduleId,
      sectionNumber: p.sectionNumber,
      instruction: p.instruction,
      content: p.content,
      wordCount: p.wordCount,
      mode: p.mode,
    }),
    [],
  );

  // Shared commit-then-write core for BOTH the manual Approve path and the
  // ``/redraft`` auto-apply path. Pure of card/phase/label state — the caller
  // owns those; this only touches the version-override ref, the flow token,
  // and the durable SSOT + Word write. Returns a discriminated outcome (or
  // ``null`` when a newer flow superseded this one mid-await) so each caller can
  // render conflicts/errors its own way. ``onBeforeWord`` runs just before the
  // Word insert (the approve path uses it to advance its apply checklist).
  const commitAndWrite = useCallback(
    async (p: AdhocProposal, token: number, onBeforeWord?: () => void): Promise<CommitOutcome | null> => {
      // Sanitized ONCE — the identical string the card previews, the
      // decision endpoint stores, and Word receives. For a draft this is the
      // FRAGMENT, not a body: the server splices it in after ``anchorText``
      // and Word inserts it at the same paragraph. Only a redraft replaces.
      const cleaned = sanitizeDraftForWord(p.content);
      const isFragment = p.mode !== "redraft";

      // 1. SSOT first.
      const result = await decideAdhocProposal(p.runId, p.moduleId, {
        action: "approve",
        expectedVersion: overrideVersionRef.current ?? p.baseSectionVersion,
        content: cleaned,
        anchorText: p.anchorText,
      });
      if (flowTokenRef.current !== token) return null;

      if (result.kind === "version_conflict") {
        return { kind: "conflict", currentVersion: result.currentVersion };
      }
      // The proposal record vanished server-side (decided elsewhere,
      // replaced, or wiped by a stale end-of-run archival) — but the
      // drafted content is right here. NEVER discard it: fall back to the
      // direct version-checked edit path. Deliberately not
      // insertProposalIntoSection (it silently retries on conflict, which
      // would defeat the two-click drift protection).
      let viaFallback = false;
      if (result.kind === "no_pending_proposal") {
        try {
          const snap = await getSectionState(p.runId);
          if (flowTokenRef.current !== token) return null;
          const section = snap.sections.find((s) => s.module_id === p.moduleId);
          if (!section) throw new Error("section not found in run state");
          // The direct edit path takes a whole BODY, so a fragment has to be
          // spliced before it goes anywhere near it — sending the fragment
          // raw here would replace the section with just the new paragraph,
          // which is exactly the failure this whole change exists to remove.
          let body = cleaned;
          if (isFragment) {
            const current = await getSectionContent(p.runId, p.moduleId);
            if (flowTokenRef.current !== token) return null;
            const existing = (current.sections ?? [])
              .map((sec) => sec.draft_content ?? "")
              .join("\n\n")
              .trim();
            body = existing ? insertAfterSpanInDraft(existing, p.anchorText ?? "", cleaned) : cleaned;
          }
          const walked = await walkToInserted(p.runId, p.moduleId, section.status);
          if (flowTokenRef.current !== token) return null;
          const direct = await submitAgentRedraft({
            runId: p.runId,
            moduleId: p.moduleId,
            content: body,
            expectedVersion: walked ?? overrideVersionRef.current ?? p.baseSectionVersion ?? section.section_version,
          });
          if (flowTokenRef.current !== token) return null;
          if (direct.conflict) {
            return { kind: "conflict", currentVersion: direct.conflict.currentVersion };
          }
          if (!direct.ok) throw new Error(direct.error ?? "direct edit failed");
          viaFallback = true;
        } catch (err) {
          if (flowTokenRef.current !== token) return null;
          const detail = err instanceof Error ? err.message : String(err);
          return {
            kind: "error",
            message: `Couldn't apply the draft — the proposal record was already cleared server-side and the direct save failed (${detail}).`,
          };
        }
      }
      if (!viaFallback && result.kind !== "approved") {
        const message = result.kind === "error" ? result.message : "unexpected response";
        return { kind: "error", message: `Couldn't apply the draft: ${message}` };
      }

      // 2. Word second. A redraft replaces the section body; a draft INSERTS
      //    its fragment at the anchor paragraph and leaves everything else
      //    byte-identical. The SSOT is already committed either way, so a
      //    failure here only means the open doc is stale.
      onBeforeWord?.();
      const sectionLabel = p.sectionNumber ? `§${p.sectionNumber}` : p.sectionLabel;
      const applied = p.mode === "redraft" ? "the redraft" : "the new content";
      let note: string;
      if (p.sectionNumber) {
        const sectionNumber = p.sectionNumber;
        let wordOk = false;
        let appendedInstead = false;
        // A redraft rewrites the whole section body: formatted EndNote citations
        // there would be deleted. Hold the Word write back (the SSOT already has
        // the redraft) and tell the writer how to proceed.
        const endNoteFields = isFragment
          ? 0
          : await enqueueWordWrite(() => countEndNoteFieldsIn(sectionNumber)).catch(() => 0);
        try {
          if (endNoteFields > 0) {
            // held back — see above
          } else if (isFragment) {
            const outcome = await enqueueWordWrite(
              () => insertFragmentInSection(sectionNumber, p.anchorText, cleaned),
              `Applying your edit to §${sectionNumber}`,
            );
            wordOk = outcome === "inserted" || outcome === "appended";
            appendedInstead = outcome === "appended";
          } else {
            
            wordOk = await enqueueWordWrite(
              () =>
                insertSectionContent({
                  sectionNumber,
                  content: cleaned,
                  scrollAfterInsert: true,
                  ...(p.sourceDocx ? { fragmentBase64: p.sourceDocx } : {}),
                }),
              `Writing §${sectionNumber}`,
            );
          }
        } catch {
          wordOk = false;
        }
        // A copied section's captions and references carry the source's
        // numbers until the fields are updated, as a run's insert does.
        if (wordOk) await enqueueWordWrite(() => refreshLiveDocFields()).catch(() => 0);
        note =
          endNoteFields > 0
            ? endNoteOverwriteMessage(endNoteFields, "section")
            : wordOk
              ? `Applied ${applied} to ${sectionLabel}.`
              : `Saved to the record — couldn't update ${sectionLabel} in the document; use Load to refresh.`;
        // Say where it actually went. The writer put their cursor somewhere
        // deliberate; silently landing elsewhere is worse than a sentence.
        if (wordOk && (appendedInstead || (result.kind === "approved" && result.anchorMissed))) {
          note += " The paragraph you had the cursor in was no longer there, so it went at the end of the section.";
        }
      } else {
        note = `Saved to the record — the section couldn't be located in the document; use Load to refresh.`;
      }
      if (viaFallback) {
        note += " (The proposal record had already been cleared server-side — saved via the direct edit path.)";
      }
      return { kind: "applied", note };
    },
    [],
  );

  const approve = useCallback(async () => {
    if (!proposal || phase !== "pending_decision") return;
    const token = ++flowTokenRef.current;
    // Stage checklist for the approve path: SSOT commit, then Word render.
    setApplyLabels([APPLY_STEP_SAVE, APPLY_STEP_WORD]);
    setApplyStepIndex(0);
    setPhase("applying");

    const outcome = await commitAndWrite(proposal, token, () => setApplyStepIndex(1));
    if (outcome === null) return; // superseded by a newer flow
    if (outcome.kind === "conflict") {
      overrideVersionRef.current = outcome.currentVersion;
      setDriftWarning(
        "This section was edited after the draft was made — approving again will overwrite those changes.",
      );
      setPhase("pending_decision");
      return;
    }
    if (outcome.kind === "error") {
      setPhase("pending_decision");
      onError(`${outcome.message} The draft is still shown above.`);
      return;
    }
    if (flowTokenRef.current !== token) return;
    onDecisionRecorded("accepted", outcome.note, record(proposal));
    setProposal(null);
    setDriftWarning(null);
    setPhase("idle");
  }, [proposal, phase, onDecisionRecorded, onError, record, commitAndWrite]);

  const autoApply = useCallback(
    async (p: AdhocProposal) => {
      // Only auto-apply from a clean idle state — if a decision/card is
      // already in flight, don't clobber it; surface this one as a card.
      if (phase !== "idle") {
        offer(p);
        return;
      }
      const token = ++flowTokenRef.current;
      overrideVersionRef.current = null;
      const outcome = await commitAndWrite(p, token);
      if (outcome === null) return; // superseded
      if (outcome.kind === "conflict") {
        // Version drift — never overwrite silently. Fall back to the manual
        // Approve/Reject card, armed with the fresh version so a deliberate
        // second Approve overwrites (mirrors the approve() drift contract).
        offer(p);
        overrideVersionRef.current = outcome.currentVersion;
        setDriftWarning(
          "This section was edited after the draft was made — approving again will overwrite those changes.",
        );
        return;
      }
      if (outcome.kind === "error") {
        // Couldn't auto-apply — surface the drafted content as a card so the
        // writer can retry via Approve rather than losing the redraft.
        offer(p);
        onError(outcome.message);
        return;
      }
      onDecisionRecorded("accepted", outcome.note, record(p));
    },
    [phase, offer, commitAndWrite, onDecisionRecorded, onError, record],
  );

  const reject = useCallback(async () => {
    if (!proposal || phase !== "pending_decision") return;
    const token = ++flowTokenRef.current;
    // Reject does no Word write — keep the checklist empty so the card
    // shows its plain busy state, not the apply stages.
    setApplyLabels([]);
    setApplyStepIndex(-1);
    setPhase("applying");
    const result = await decideAdhocProposal(proposal.runId, proposal.moduleId, { action: "reject" });
    if (flowTokenRef.current !== token) return;
    const sectionLabel = proposal.sectionNumber ? `§${proposal.sectionNumber}` : proposal.sectionLabel;
    const what = proposal.mode === "redraft" ? "redraft" : "draft";
    const note =
      result.kind === "rejected" || result.kind === "no_pending_proposal"
        ? `Rejected the proposed ${what} for ${sectionLabel}.`
        : `Rejected the proposed ${what} for ${sectionLabel} (the backend record couldn't be updated — it may reappear on reload).`;
    onDecisionRecorded("rejected", note, record(proposal));
    setProposal(null);
    setDriftWarning(null);
    setPhase("idle");
  }, [proposal, phase, onDecisionRecorded, record]);

  // ── Chat "draft content": the in-request drafting stream ────────────
  // This is the half that used to be a whole pipeline RUN. It is one HTTP
  // stream now, so the flow owns the "requesting" phase its docstring used to
  // say it lacked, and Stop is an abort rather than a Redis cancel latch.
  const requestFragment = useCallback(
    async (req: FragmentDraftRequest) => {
      const token = ++flowTokenRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      overrideVersionRef.current = null;
      setProposal(null);
      setDriftWarning(null);
      setApplyLabels([]);
      setApplyStepIndex(-1);
      setProgressLabel("Drafting the new content…");
      setPhase("requesting");

      let result: FragmentDraftResult | null = null;
      try {
        await requestFragmentDraftStream(
          req.runId,
          req.moduleId,
          {
            instruction: req.instruction,
            anchorText: req.anchorText,
            sectionNumber: req.sectionNumber,
            libraryId: req.libraryId ?? null,
            docType: req.docType ?? null,
            attachments: req.attachments,
          },
          (event) => {
            if (flowTokenRef.current !== token) return;
            if (event.type === "complete") {
              result = event as unknown as FragmentDraftResult;
              return;
            }
            if (event.type === "error") {
              throw new Error(String(event.error ?? "drafting failed"));
            }
            // Everything else is progress. The mapper is the one the
            // highlight-edit flow already uses, so the two read identically.
            const label = labelForSelectionEditEvent(event, "Drafting the new content…");
            if (label) setProgressLabel(label);
          },
          controller.signal,
        );
      } catch (err) {
        if (flowTokenRef.current !== token) return;
        setPhase("idle");
        setProgressLabel(null);
        // An abort is the writer pressing Stop, not a failure to report.
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          onError(`Couldn't draft the content: ${err instanceof Error ? err.message : String(err)}`);
        }
        return;
      }
      if (flowTokenRef.current !== token) return;

      if (!result) {
        // Stream ended with no terminal event — a cancel, or the server gave
        // up quietly. Either way there is nothing to offer.
        setPhase("idle");
        setProgressLabel(null);
        return;
      }
      const done: FragmentDraftResult = result;
      setProgressLabel(null);
      offer({
        runId: req.runId,
        moduleId: req.moduleId,
        sectionNumber: done.section_number ?? req.sectionNumber,
        sectionLabel: req.sectionLabel,
        instruction: req.instruction,
        content: done.content,
        wordCount: done.word_count,
        baseSectionVersion: done.section_version,
        mode: "draft",
        anchorText: done.anchor_text ?? req.anchorText,
      });
      if (!done.persisted) {
        // The card is live, but a pane reload won't bring it back and Approve
        // will take the direct-edit fallback. Say so rather than let the
        // writer discover it.
        onError(
          "This draft couldn't be saved to the record — it will not come back if you reload the pane.",
        );
      }
    },
    [offer, onError],
  );

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    flowTokenRef.current++;
    setPhase("idle");
    setProgressLabel(null);
  }, []);

  // Project stage labels + active index into the checklist; gated on the
  // applying phase so a reverted flow never shows stale steps.
  const applySteps: ApplyStep[] =
    phase === "applying"
      ? applyLabels.map((label, i) => ({
          id: `adhoc-apply-${i}`,
          label,
          state: i < applyStepIndex ? "done" : i === applyStepIndex ? "active" : "pending",
        }))
      : [];

  return {
    phase,
    proposal,
    progressLabel,
    driftWarning,
    applySteps,
    offer,
    autoApply,
    requestFragment,
    cancel,
    approve,
    reject,
  };
}
